import { CheckCircle2, Info, X, XCircle } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type ToastTone = "success" | "error" | "info";
interface ToastItem { id: number; title: string; message?: string; tone: ToastTone }

const ToastContext = createContext<((title: string, options?: { message?: string; tone?: ToastTone }) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const show = useCallback((title: string, options?: { message?: string; tone?: ToastTone }) => {
    const id = Date.now() + Math.random();
    setItems((current) => [...current, { id, title, message: options?.message, tone: options?.tone ?? "success" }]);
    window.setTimeout(() => setItems((current) => current.filter((item) => item.id !== id)), 4200);
  }, []);
  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {items.map((item) => (
          <div className={`toast toast-${item.tone}`} key={item.id}>
            <span>{item.tone === "success" ? <CheckCircle2 size={18} /> : item.tone === "error" ? <XCircle size={18} /> : <Info size={18} />}</span>
            <div><strong>{item.title}</strong>{item.message && <p>{item.message}</p>}</div>
            <button type="button" aria-label="Dismiss" onClick={() => setItems((current) => current.filter((candidate) => candidate.id !== item.id))}><X size={15} /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
}
